import { Badge, Card, ErrorBanner } from '@zarya/ui';
import { Icon } from '@zarya/theme';
import { dataHour } from '@app/format';
import { useIdentityContracts } from './Contracts.api';
import type { ContractSummary } from './ContractsModel';
import s from './Contracts.module.css';
import { t } from '@app/i18n';

/**
 * `Record` **fechado** no enum de `ContractSummary.fechamento`, e não `Record<string, …>`.
 *
 * Com a chave solta, um valor novo do contrato saía como `undefined` dentro do badge —
 * um selo em branco na linha do histórico, sem nada vermelho em lugar nenhum. Era o caso
 * de `ambiente_nao_produtivo` (27/08) e seria o de `superada`, `onboarding_automatico` e
 * `reducao` (29/08); com o tipo fechado o `tsc` cobra o rótulo quando o enum cresce, que
 * é como T9 já fazia.
 */
/**
 * O `closure` do contrato → rótulo do badge.
 *
 * O mapa não guarda mais a frase, e sim a própria chave do enum: o valor vem do
 * SERVIDOR e não se traduz; o texto sai de `contracts.closure.<valor>` em tempo de
 * desenho. Um `closure` novo no contrato aparece como a chave crua na tela — que é
 * o comportamento do catálogo, e é preferível a um badge vazio.
 */
const CLOSURE_KEYS = [
  'approved', 'code_only', 'capability_removed', 'environment_exempt',
  'automatic_onboarding', 'already_covered', 'resource_removed', 'superseded',
  'trusted_requester',
] as const satisfies readonly NonNullable<ContractSummary['closure']>[];

/** Exportado para o teste percorrer os valores, e não uma cópia deles. */
export const CLOSURES = CLOSURE_KEYS;

const closureLabel = (closure: NonNullable<ContractSummary['closure']>) =>
  t(`contracts.closure.${closure}`);

export interface ContractReleasesCardProps {
  identityId: string;
  /** Versão exibida no card de texto — a linha fica marcada. */
  selecionado: string | null;
  onSelecionar: (contractId: string | null) => void;
}

/**
 * Card **Releases** (FR-070): o histórico de contratos da identidade. Clicar
 * numa versão antiga a mostra no card de texto; o vigente volta pelo botão.
 */
export function ContractReleasesCard({
  identityId,
  selecionado,
  onSelecionar,
}: ContractReleasesCardProps) {
  const history = useIdentityContracts(identityId);
  const itens = (history.data?.items ?? []) as ContractSummary[];

  return (
    <Card title={t('contracts.releases.title')}>
      {history.isLoading ? <p className={s.carregando}>{t('contracts.releases.loading')}</p> : null}

      {history.isError ? (
        <ErrorBanner
          error={history.error}
          title={t('contracts.releases.error')}
          onRetry={() => void history.refetch()}
          hideLink
        />
      ) : null}

      {!history.isLoading && !itens.length ? (
        <p className={s.muted}>{t('contracts.releases.empty')}</p>
      ) : (
        <div className={s.releases}>
          {itens.map((versao) => {
            const ativo = versao.state === 'ativo';
            const marked = selecionado === versao.id || (ativo && !selecionado);
            return (
              <button
                type="button"
                key={versao.id}
                className={[s.release, marked ? s.releaseOn : ''].filter(Boolean).join(' ')}
                onClick={() => onSelecionar(ativo ? null : versao.id)}
              >
                <Icon name={ativo ? 'check-circle' : 'clock'} size="sm" aria-hidden="true" />
                <span className={s.releaseTexto}>
                  <b>
                    v{versao.version} · {versao.digest.slice(0, 24)}…
                  </b>
                  <span>
                    {versao.approved_by} · {dataHour(versao.approved_at)}
                  </span>
                </span>
                <Badge className={s.releaseBadge} tone={ativo ? 'success' : 'neutral'}>
                  {ativo ? t('contracts.releases.active') : t('contracts.releases.replaced')}
                </Badge>
                {versao.closure ? (
                  <Badge className={s.releaseBadge} tone="neutral">{closureLabel(versao.closure)}</Badge>
                ) : null}
              </button>
            );
          })}
        </div>
      )}
    </Card>
  );
}
